import { initializeApp } from "firebase/app";
import {
  getFirestore,
  doc,
  getDoc,
  deleteDoc,
  runTransaction,
  serverTimestamp,
  addDoc,
  collection,
} from "firebase/firestore";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

const firebaseConfig = {
  apiKey: "AIzaSyAEsrHK3k5gCS7ZnXXlvsoqz0DZFKFwjTk",
  authDomain: "pushnotification-26fdb.firebaseapp.com",
  projectId: "pushnotification-26fdb",
  storageBucket: "pushnotification-26fdb.firebasestorage.app",
  messagingSenderId: "687591429394",
  appId: "1:687591429394:web:2efc71c01eefc60035cfcb",
  measurementId: "G-V2B83EBP07",
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const messaging = getMessaging(app);

// ─── FCM ───────────────────────────────────────────────────────────────────
export const getFcmToken = async () => {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;
    return await getToken(messaging, {
      vapidKey:
        "BAVE9PdQhTayMEIwvmmDkQ9ZSKDNcAW7nmkcVopCbEEvM4jX370bcwjNiwSfjKJ4mPDNiXpXSr_0m1Eoxk1bwaI",
    });
  } catch (err) {
    console.error("FCM Token Error", err);
    return null;
  }
};

export const onForegroundMessage = (cb) => onMessage(messaging, cb);

// ─── CALL STATUS ENUM ──────────────────────────────────────────────────────
export const CALL_STATUS = {
  RINGING: 1,
  IN_CALL: 2,
  REJECTED: -1,
  ENDED: -2,
};

/**
 * Create a call notification document in Firestore.
 *
 * Uses a transaction so that if a doc already exists for this roomId
 * (e.g. an earlier call or a concurrent write), we skip silently.
 *
 * @param {object} params
 * @param {string} params.callerId      - userId of the person who initiated the call
 * @param {string} params.callerName    - display name of the caller
 * @param {string} params.callerRole    - "doctor" | "patient"
 * @param {string} params.receiverId    - userId of the other party
 * @param {string} params.roomId        - appointmentId (used as Firestore doc ID)
 */
export const createCallNotification = async ({
  callerId,
  callerName,
  callerRole,
  receiverId,
  roomId,
}) => {
  const callRef = doc(db, "calls", roomId);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(callRef);
    if (snap.exists()) {
      // A record already exists — caller is already waiting or call is active.
      // Do nothing; avoids duplicate notifications.
      return;
    }
    tx.set(callRef, {
      incomingCall: {
        callerName,
        callerId,
        role: callerRole,
        roomId,
        receiverId,
        status: CALL_STATUS.RINGING,
        createdAt: serverTimestamp(),
      },
    });
  });
};

// ─── BACKWARD-COMPATIBLE ALIASES ──────────────────────────────────────────
// GiveConsent.jsx, UpcomingAppointments.jsx, and AppointmentTable.jsx still
// import the old names.  Re-export them here so those files compile without
// any changes.

/**
 * Patient app: patient initiates → notifies the doctor.
 * Alias of createCallNotification with role pre-filled as "patient".
 *
 * @param {string} patientId    callerId
 * @param {string} roomId       appointmentId
 * @param {string} patientName  callerName
 * @param {string} doctorId     receiverId
 */
export const notifyDoctor = async (patientId, roomId, callerName, doctorId) =>
  createCallNotification({
    callerId: patientId,
    callerName: callerName,
    callerRole: "patient",
    receiverId: doctorId,
    roomId,
  });

/**
 * Delete the call document from Firestore.
 * Only the original caller (matched by callerId) may delete it.
 *
 * @param {string} roomId        - Firestore doc ID (== appointmentId)
 * @param {string} callerId      - The caller's userId to validate ownership
 */
export const deleteCallRecord = async (roomId, callerId) => {
  if (!roomId || !callerId) return;
  try {
    const callRef = doc(db, "calls", roomId);
    const snap = await getDoc(callRef);
    if (!snap.exists()) return; // already gone — nothing to do

    const storedCallerId = snap.data()?.incomingCall?.callerId;
    if (storedCallerId !== callerId) {
      // Not the caller; do not delete (receiver handles their own flow).
      console.warn("deleteCallRecord: caller mismatch — skipped.");
      return;
    }
    await deleteDoc(callRef);
  } catch (err) {
    console.error("deleteCallRecord error:", err);
  }
};

/*** Notify doctor that patient uploaded a diet image. */
export const notifyDietImageUpload = async ({
  id,
  patientId,
  patientName,
  doctorId,        // payload.userId
  consultationId,  // payload.consultationId
  chartTitle,      // payload.chartTitle
  meal,            // patientDietPlanDetails item .meal
  mealTime,        // patientDietPlanDetails item .mealTime  { hour, minute }
  dayTitle,        // patientDietPlanDetails item .dayTitle
  compliance,
}) => {
  try {
    await addDoc(collection(db, "dietImageUploads"), {
      id,
      patientId,
      patientName,
      doctorId,
      consultationId,
      chartTitle,
      meal,
      mealTime,       // { hour, minute, second, nano }
      dayTitle,
      seen: false,
      createdAt: serverTimestamp(),
      compliance
    });
  } catch (err) {
    console.error("notifyDietImageUpload error:", err);
  }
};
